/**
 * Case-time rules for the pill's live stretch (ENG-202).
 *
 * A line-for-line port of the case-time part of the backend's
 * `case_time.active_seconds()` (web-demo-backend `src/case_time.py`), so the
 * pill counts the not-yet-recorded stretch exactly as the backend will. The
 * shared fixtures in `__fixtures__/case_time_active_seconds.json` keep the two
 * from drifting. Per-annotation time stays backend-only.
 *
 * Per tab (page_session_id), a gap a → b between consecutive events counts if
 * a isn't tab_hidden and the gap is ≤ the idle cap (longer gaps are dropped
 * entirely); closed spans (complete or unassessable open) are cut out; tabs are
 * then unioned so overlapping time counts once. Times are ms since epoch.
 */
import type { CaseTimeEventType } from "./caseEventQueue";

export interface CaseTimeEvent {
  event_id: string;
  type: CaseTimeEventType | string;
  client_ts: string;
  page_session_id: string;
}

/** [start, end) in ms since epoch. */
export type Interval = readonly [number, number];

const CLOSE_OPENERS: Record<string, string> = {
  mark_complete: "complete",
  mark_unassessable: "unassessable",
};
const CLOSE_CLOSERS: Record<string, string> = {
  unmark: "complete",
  clear_unassessable: "unassessable",
};

interface TimedEvent {
  event_id: string;
  type: string;
  ts: number;
  session: string;
}

function seconds(intervals: Iterable<Interval>): number {
  let total = 0;
  for (const [start, end] of intervals) total += end - start;
  return total / 1000;
}

/** Merge overlapping or touching intervals. */
export function union(intervals: Iterable<Interval>): Interval[] {
  const sorted = [...intervals]
    .filter(([start, end]) => end > start)
    .sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const merged: [number, number][] = [];
  for (const [start, end] of sorted) {
    const last = merged.at(-1);
    if (last && start <= last[1]) last[1] = Math.max(last[1], end);
    else merged.push([start, end]);
  }
  return merged;
}

/** interval minus holes (holes sorted and non-overlapping). */
function subtract(interval: Interval, holes: readonly Interval[]): Interval[] {
  let [start] = interval;
  const end = interval[1];
  const out: Interval[] = [];
  for (const [holeStart, holeEnd] of holes) {
    if (holeEnd <= start || holeStart >= end) continue;
    if (holeStart > start) out.push([start, holeStart]);
    start = Math.max(start, holeEnd);
    if (start >= end) return out;
  }
  if (start < end) out.push([start, end]);
  return out;
}

function dedupeSorted(events: Iterable<CaseTimeEvent>): TimedEvent[] {
  const timed: TimedEvent[] = [];
  for (const event of events) {
    const ts = Date.parse(event.client_ts);
    if (Number.isNaN(ts)) continue;
    timed.push({ event_id: event.event_id, type: event.type, ts, session: event.page_session_id });
  }
  timed.sort((a, b) =>
    a.ts !== b.ts ? a.ts - b.ts : a.event_id < b.event_id ? -1 : a.event_id > b.event_id ? 1 : 0,
  );
  const seen = new Set<string>();
  const out: TimedEvent[] = [];
  for (const event of timed) {
    if (seen.has(event.event_id)) continue;
    seen.add(event.event_id);
    out.push(event);
  }
  return out;
}

/** Spans when the case is closed, case-wide, and whether it is closed now. */
function closedSpans(events: readonly TimedEvent[]): { spans: Interval[]; closedNow: boolean } {
  const openKinds = new Set<string>();
  const spans: Interval[] = [];
  let since: number | null = null;
  for (const event of events) {
    const opens = CLOSE_OPENERS[event.type];
    const closes = CLOSE_CLOSERS[event.type];
    if (opens) {
      if (openKinds.size === 0) since = event.ts;
      openKinds.add(opens);
    } else if (closes) {
      openKinds.delete(closes);
      if (openKinds.size === 0 && since !== null) {
        spans.push([since, event.ts]);
        since = null;
      }
    }
  }
  if (openKinds.size > 0 && since !== null) spans.push([since, Number.POSITIVE_INFINITY]);
  return { spans, closedNow: openKinds.size > 0 };
}

function sessionIntervals(
  sessionEvents: readonly TimedEvent[],
  idleCapS: number,
  closed: readonly Interval[],
): Interval[] {
  const out: Interval[] = [];
  for (let i = 0; i + 1 < sessionEvents.length; i++) {
    const a = sessionEvents[i];
    const b = sessionEvents[i + 1];
    if (a.type === "tab_hidden") continue;
    if ((b.ts - a.ts) / 1000 > idleCapS) continue;
    out.push(...subtract([a.ts, b.ts], closed));
  }
  return union(out);
}

function derive(events: Iterable<CaseTimeEvent>, idleCapS: number) {
  const ordered = dedupeSorted(events);
  const { spans, closedNow } = closedSpans(ordered);
  const sessions = new Map<string, TimedEvent[]>();
  for (const event of ordered) {
    const list = sessions.get(event.session);
    if (list) list.push(event);
    else sessions.set(event.session, [event]);
  }
  const intervals: Interval[] = [];
  for (const sessionEvents of sessions.values()) {
    intervals.push(...sessionIntervals(sessionEvents, idleCapS, spans));
  }
  return {
    intervals: union(intervals),
    closed: closedNow,
    lastTs: ordered.length ? ordered[ordered.length - 1].ts : null,
  };
}

/** Counted intervals (union across tabs), as the backend derives them. */
export function countedIntervals(events: Iterable<CaseTimeEvent>, idleCapS: number): Interval[] {
  return derive(events, idleCapS).intervals;
}

/** Case seconds and whether the case is closed now (the backend's case_seconds / closed). */
export function caseSeconds(
  events: Iterable<CaseTimeEvent>,
  idleCapS: number,
): { seconds: number; closed: boolean; lastTs: number | null } {
  const { intervals, closed, lastTs } = derive(events, idleCapS);
  return { seconds: seconds(intervals), closed, lastTs };
}

/** Seconds of `intervals` after `afterMs` (the part the backend hasn't counted yet). */
export function secondsAfter(intervals: Iterable<Interval>, afterMs: number): number {
  const clipped: Interval[] = [];
  for (const [start, end] of intervals) {
    if (end > afterMs) clipped.push([Math.max(start, afterMs), end]);
  }
  return seconds(clipped);
}
